import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogService } from '../service/blogService';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import MetalRateStrip from '../components/MetalRateStrip';
import './BlogListPage.css';

const CATEGORIES = [
  { label: 'All Articles', slug: 'all' },
  { label: 'Compliance & Hallmarking', slug: 'compliance' },
  { label: 'Bullion & Pricing', slug: 'pricing' },
  { label: 'Manufacturing Tech', slug: 'technology' },
];

const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      const data = await blogService.getPublishedPosts(selectedCategory);
      setPosts(data);
      setLoading(false);
    };
    fetchPosts();
  }, [selectedCategory]);

  const filteredPosts = posts.filter((p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      p.title?.toLowerCase().includes(q) ||
      p.excerpt?.toLowerCase().includes(q) ||
      p.author?.toLowerCase().includes(q) ||
      p.tags?.some((t) => t.toLowerCase().includes(q))
    );
  });

  const featuredPost = filteredPosts[0];
  const remainingPosts = filteredPosts.slice(1);

  return (
    <div className="app-wrapper blog-wrapper">
      <MetalRateStrip />
      <Navbar />

      <main className="blog-main-content">
        {/* Header Hero */}
        <section className="blog-hero-section">
          <div className="jmi-container">
            <div className="blog-hero-badge">
              <span className="badge-sparkle">✦</span>
              <span>B2B JEWELLERY INTELLIGENCE & INDUSTRY JOURNAL</span>
            </div>
            <h1 className="blog-hero-title">
              Market Reports, Regulatory Standards & <span className="gold-gradient-text">Wholesale Trade Insights</span>
            </h1>
            <p className="blog-hero-subtitle">
              Actionable intelligence for retail jewellery showroom owners, bullion dealers, casting manufacturers, 
              and regional trade operators across India.
            </p>

            {/* Search & Category Filter Bar */}
            <div className="blog-filter-bar">
              <div className="blog-category-pills">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.slug}
                    className={`cat-pill ${selectedCategory === cat.slug ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(cat.slug)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              <div className="blog-search-wrap">
                <input
                  type="text"
                  placeholder="Search articles by topic, HUID, gold rates, CAD..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Blog Content Section */}
        <section className="blog-catalog-section">
          <div className="jmi-container">
            {loading ? (
              <div className="blog-loading-state">
                <div className="spinner"></div>
                <span>Loading jewellery intelligence articles...</span>
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="blog-empty-state">
                <h3>No articles found</h3>
                <p>Try switching categories or clearing your search term.</p>
                <button className="btn btn-secondary" onClick={() => { setSelectedCategory('all'); setSearch(''); }}>
                  View All Articles
                </button>
              </div>
            ) : (
              <>
                {/* Featured Hero Article */}
                {featuredPost && (
                  <article className="featured-article-card">
                    <div className="featured-img-wrap">
                      <img 
                        src={featuredPost.coverImage || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=1200&q=80'} 
                        alt={featuredPost.title} 
                      />
                      <span className="featured-badge">FEATURED ANALYSIS</span>
                    </div>

                    <div className="featured-content">
                      <div className="article-meta-row">
                        <span className="category-tag">{featuredPost.category}</span>
                        <span className="meta-separator">•</span>
                        <span className="read-time">{featuredPost.readTime || '6 min read'}</span>
                      </div>

                      <h2 className="featured-title">
                        <Link to={`/blog/${featuredPost.slug}`}>
                          {featuredPost.title}
                        </Link>
                      </h2>

                      <p className="featured-excerpt">{featuredPost.excerpt}</p>

                      <div className="featured-footer">
                        <div className="author-block">
                          <div className="author-avatar">{featuredPost.author?.[0] || 'J'}</div>
                          <div>
                            <div className="author-name">{featuredPost.author}</div>
                            <div className="author-role">{featuredPost.authorRole || 'JMI Research'}</div>
                          </div>
                        </div>

                        <Link to={`/blog/${featuredPost.slug}`} className="btn-read-more">
                          Read Full Report →
                        </Link>
                      </div>
                    </div>
                  </article>
                )}

                {/* Grid of Remaining Articles */}
                {remainingPosts.length > 0 && (
                  <div className="articles-grid">
                    {remainingPosts.map((post) => (
                      <article key={post.slug || post.id} className="article-card">
                        <div className="article-card-img">
                          <img 
                            src={post.coverImage || 'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?auto=format&fit=crop&w=800&q=80'} 
                            alt={post.title} 
                          />
                          <span className="category-tag-floating">{post.category}</span>
                        </div>

                        <div className="article-card-body">
                          <div className="card-meta">
                            <span>{post.readTime || '5 min read'}</span>
                            <span>•</span>
                            <span>{new Date(post.publishedAt || post.createdAt || Date.now()).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>

                          <h3 className="card-title">
                            <Link to={`/blog/${post.slug}`}>
                              {post.title}
                            </Link>
                          </h3>

                          <p className="card-excerpt">{post.excerpt}</p>

                          <div className="card-footer">
                            <span className="card-author">By {post.author}</span>
                            <Link to={`/blog/${post.slug}`} className="card-link">
                              Read →
                            </Link>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Newsletter & Editorial CTA */}
            <div className="blog-newsletter-banner">
              <div className="newsletter-content">
                <span className="newsletter-sparkle">📩</span>
                <div>
                  <h3>Subscribe to Weekly Bullion & Jewellery Trade Bulletins</h3>
                  <p>Get instant WhatsApp & email digests of BIS regulations, spot rate fluctuations, and trade policy updates.</p>
                </div>
              </div>
              <div className="newsletter-input-group">
                <input type="tel" placeholder="Enter 10-digit mobile number" />
                <button type="button" className="btn btn-primary">
                  Subscribe Free
                </button>
              </div>
            </div>

            {/* Author Portal Footer Link */}
            <div className="author-portal-shortcut">
              <span>Are you a registered JMI Editorial Contributor or Bullion Analyst?</span>
              <Link to="/blog-portal/login" className="author-portal-link">
                Access Author Portal Studio ✍️
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogListPage;
